import React, { useState, useEffect, useRef } from 'react';
import { Chip, TextField, Stack, Box, LinearProgress } from '@mui/material';
import voucherOutItemServices from '../voucherOutItemServices';
import { useAlert } from '../../../../../../context/AlertContext';

function VoucherOutItemSerialNumber({ voucherItem, isPrinting }) {
    const { AppAlert } = useAlert();
    const [inputSerialNumber, setInputSerialNumber] = useState("");
    const [saving, setSavingStatus] = useState(false);
    
    // Reference for the input field to trigger autofocus
    const inputRef = useRef(null);

    // 1. Manage local state for serial numbers (initialized from prop)
    const [serials, setSerials] = useState(voucherItem?.serialNumbers || []);

    // Sync local state if parent updates the prop
    useEffect(() => {
        setSerials(voucherItem?.serialNumbers || []);
    }, [voucherItem]);

    // Focus input field whenever saving finishes and input becomes visible again
    useEffect(() => {
        if (!saving && inputRef.current) {
            inputRef.current.focus();
        }
    }, [saving]);

    // 2. Completed Reload Serial Numbers Function
    const reloadSerialNumbers = () => {
        setSavingStatus(true);
        voucherOutItemServices.getSerialNumberByVoucherItemId(voucherItem.id)
            .then(res => {
                // Check if API returned data successfully
                if (res?.data) {
                    setSerials(res.data);
                } else if (Array.isArray(res)) {
                    setSerials(res);
                }
            })
            .catch(error => {
                AppAlert(error?.msg || "Failed to reload serial numbers", "error");
            })
            .finally(() => {
                setSavingStatus(false);
            });
    };

    // Submit handler
    const handleSubmit = async () => {
        const trimmedSerial = inputSerialNumber.trim();
        if (!trimmedSerial) return;

        const payload = {
            voucherId: voucherItem.voucherId,
            voucherItemId: voucherItem.id,
            serialNumber: trimmedSerial
        };

        setSavingStatus(true);
        voucherOutItemServices.addSerialNumber(payload)
            .then(res => {
                AppAlert(res.msg || "Serial added successfully");
                setInputSerialNumber("");
                reloadSerialNumbers(); // Reload list on success
            })
            .catch(error => {
                AppAlert(error?.msg || "Error adding serial", "error");
                setSavingStatus(false);
            });
    };

    // Keyboard Handler (triggers on 'Enter')
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
        }
    };

    // Delete serial handler
    const handleDelete = (serialId) => {
        setSavingStatus(true);
        voucherOutItemServices.removeSerialNumber(serialId)
            .then(res => {
                AppAlert(res.msg || "Serial removed successfully");
                reloadSerialNumbers(); // Reload list on success
            })
            .catch(error => {
                AppAlert(error?.msg || "Error removing serial", "error");
                setSavingStatus(false);
            });
    };

    return (
        <Box sx={{ width: '100%' }}>
            {/* Display list of Serial Numbers as Chips */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                {serials.length > 0 && 
                    <span className='fw-bold'>Serial #</span>
                }
                {serials.length > 0 ? (
                    serials.map((item) => (
                        <Chip
                            key={item.id}
                            label={item.serialNumber.toUpperCase()}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: 'black', color: 'black', borderRadius: 0 }} 
                            onDelete={!isPrinting ? () => handleDelete(item.id) : undefined}
                            disabled={saving}
                        />
                    ))
                ) : (
                    isPrinting && <span style={{ fontSize: '12px', color: '#666' }}></span>
                )}
            </Stack>

            {/* Input box / Loader - Hidden during printing or when item qty limit reached */}
            {!isPrinting && voucherItem.qty > serials?.length && (
                <>
                    {saving ? (
                        <LinearProgress sx={{ maxWidth: 300 }} />
                    ) : (
                        <TextField
                            inputRef={inputRef}
                            size="small"
                            variant="standard"
                            className='bg-white'
                            placeholder="Enter Serial Number"
                            value={inputSerialNumber}
                            onChange={(e) => setInputSerialNumber(e.target.value.toUpperCase())}
                            onKeyDown={handleKeyDown}
                            fullWidth
                            sx={{ maxWidth: 300, my:0 }}
                        />
                    )}
                </>
            )}
        </Box>
    );
}

export default VoucherOutItemSerialNumber;